
import { StarRating } from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useState } from "react";
import { WriteReview, ReviewFormData } from "./WriteReview";

interface RatingOverviewProps {
  averageRating: number;
  totalReviews: number;
  ratingDistribution: {
    star5: number;
    star4: number;
    star3: number;
    star2: number;
    star1: number;
  };
  onShowAllReviews?: () => void;
  canReview?: boolean;
  reviewTarget?: {
    id: string;
    name: string;
    avatarUrl?: string;
    serviceTitle: string;
  };
}

export function RatingOverview({
  averageRating,
  totalReviews,
  ratingDistribution,
  onShowAllReviews,
  canReview = false,
  reviewTarget,
}: RatingOverviewProps) {
  const [writeReviewOpen, setWriteReviewOpen] = useState(false);

  const handleReviewSubmit = (data: ReviewFormData) => {
    console.log("Review submitted:", data);
    // In a real app, we'd save this to the backend
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-4 flex flex-col items-center justify-center p-4 border-r border-gray-100">
            <div className="text-4xl font-bold">{averageRating.toFixed(1)}</div>
            <StarRating value={averageRating} readOnly size="md" className="my-2" />
            <div className="text-sm text-gray-500">{totalReviews} reviews</div>
            
            {canReview && reviewTarget && (
              <Button
                onClick={() => setWriteReviewOpen(true)}
                variant="outline"
                className="mt-4"
              >
                Write a Review
              </Button>
            )}
          </div>
          
          <div className="md:col-span-8 space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-16 text-sm text-right">5 stars</div>
              <Progress 
                value={(ratingDistribution.star5 / totalReviews) * 100} 
                className="h-2" 
              />
              <div className="w-10 text-xs text-gray-500">
                {ratingDistribution.star5}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="w-16 text-sm text-right">4 stars</div>
              <Progress 
                value={(ratingDistribution.star4 / totalReviews) * 100} 
                className="h-2" 
              />
              <div className="w-10 text-xs text-gray-500">
                {ratingDistribution.star4}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="w-16 text-sm text-right">3 stars</div>
              <Progress 
                value={(ratingDistribution.star3 / totalReviews) * 100} 
                className="h-2" 
              />
              <div className="w-10 text-xs text-gray-500">
                {ratingDistribution.star3}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="w-16 text-sm text-right">2 stars</div>
              <Progress 
                value={(ratingDistribution.star2 / totalReviews) * 100} 
                className="h-2" 
              />
              <div className="w-10 text-xs text-gray-500">
                {ratingDistribution.star2}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="w-16 text-sm text-right">1 star</div>
              <Progress 
                value={(ratingDistribution.star1 / totalReviews) * 100} 
                className="h-2" 
              />
              <div className="w-10 text-xs text-gray-500">
                {ratingDistribution.star1}
              </div>
            </div>
            
            {onShowAllReviews && totalReviews > 0 && (
              <div className="pt-3 flex justify-end">
                <Button 
                  variant="link" 
                  onClick={onShowAllReviews}
                  className="text-anyskill-purple"
                >
                  See all reviews
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
      
      {canReview && reviewTarget && (
        <WriteReview
          open={writeReviewOpen}
          onOpenChange={setWriteReviewOpen}
          onSubmit={handleReviewSubmit}
          reviewTarget={reviewTarget}
        />
      )}
    </Card>
  );
}
